import { useState, useCallback, useEffect, useRef } from "react";
import { MatchExerciseContent } from "../../../../../types/newtypes";
import { useScoreTracking } from "../../../../../context/ScoreTrackingContext";

interface UseMatchExercisePreviewProps {
  content: MatchExerciseContent;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
  cardId: string;
}

export const useMatchExercisePreview = ({
  content,
  onCardStateChange,
  cardId,
}: UseMatchExercisePreviewProps) => {
  const [matches, setMatches] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<
    Record<string, "correct" | "incorrect" | null>
  >({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [allMatched, setAllMatched] = useState(false);
  const [showInstruction, setShowInstruction] = useState(true);
  const [shuffledPairs, setShuffledPairs] = useState<
    Array<{ item: string; match: string; correct_feedback?: string }>
  >([]);
  // Separate shuffle order for drop zones to break positional correlation
  const [shuffledDropZoneIndices, setShuffledDropZoneIndices] = useState<number[]>([]);

  // Score tracking hooks
  const { incrementMistakes, updateCardScore, isCardCompleted, getMistakes } =
    useScoreTracking();
  const maxScore = content?.max_score ?? 0;
  const previousCardIdRef = useRef<string | null>(null);

  // Reset state when cardId changes (for back-to-back match_exercise cards)
  useEffect(() => {
    // Only reset if we're actually transitioning to a different card
    if (
      previousCardIdRef.current !== null &&
      previousCardIdRef.current !== cardId
    ) {
      setMatches({});
      setFeedback({});
      setActiveId(null);
      setAllMatched(false);
      setShowInstruction(true);
      // Don't call onCardStateChange here as it would disable the next button
    }
    previousCardIdRef.current = cardId;
  }, [cardId]);

  // Helper to extract string from potentially nested object
  // Handles cases where text might be {education: "actual text", fallback: "text"} instead of "actual text"
  const extractString = (value: any): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      // Try sector-specific keys first (education, finance, care), then fallback
      const sectorKeys = ['education', 'fallback', 'finance', 'care'];
      for (const key of sectorKeys) {
        if (typeof value[key] === 'string') return value[key];
      }
      // If single key with string value, use it
      const keys = Object.keys(value);
      if (keys.length === 1) {
        const nested = value[keys[0]];
        if (typeof nested === 'string') return nested;
      }
      // Fallback: return empty string to avoid rendering objects
      return '';
    }
    return String(value || '');
  };

  // Shuffle pairs once on mount/content change to randomize initial draggable items
  useEffect(() => {
    let pairs: Array<{ item: string; match: string; correct_feedback?: string }> = [];

    // Support multiple data formats:
    // 1. Legacy v1: content.pairs with {item, match}
    // 2. Legacy v2: content.pairs with {draggable_text, correct_drop_target_label, correct_feedback: object}
    // 3. New: content.items + content.targets + content.correct_matches
    if (Array.isArray(content?.pairs) && content.pairs.length > 0) {
      // Handle legacy formats - check for both field naming conventions
      pairs = content.pairs.map((p: any) => ({
        // Support both 'item' and 'draggable_text' field names
        item: extractString(p?.item || p?.draggable_text),
        // Support both 'match' and 'correct_drop_target_label' field names
        match: extractString(p?.match || p?.correct_drop_target_label),
        // Extract string from correct_feedback (may be object with sector keys)
        correct_feedback: extractString(p?.correct_feedback)
      }));
    } else if (content?.items && content?.targets && content?.correct_matches) {
      // Transform new format to pairs format
      const correctMatches = content.correct_matches as Record<string, string>;
      const targets = content.targets;
      pairs = content.items.map((item: { id: string; text: any; correct_feedback?: string }) => {
        const targetId = correctMatches[item.id];
        const target = targets?.find((t: { id: string; label: any }) => t.id === targetId);
        return {
          item: extractString(item.text),
          match: extractString(target?.label) || '',
          correct_feedback: item.correct_feedback
        };
      });
    }

    // Fisher-Yates shuffle for draggable items
    for (let i = pairs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
    }
    setShuffledPairs(pairs);

    // Create a separate shuffle for drop zone display order
    // This breaks the positional correlation between draggable items and drop zones
    const dropZoneIndices = Array.from({ length: pairs.length }, (_, i) => i);

    // Keep shuffling until we have a different order than identity
    // This guarantees items and drop zones won't have positional correlation
    let maxAttempts = 10;
    let hasValidShuffle = false;
    while (!hasValidShuffle && maxAttempts > 0) {
      // Fisher-Yates shuffle
      for (let i = dropZoneIndices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [dropZoneIndices[i], dropZoneIndices[j]] = [dropZoneIndices[j], dropZoneIndices[i]];
      }
      // Check if at least half the positions are different from identity
      // This prevents trivial matching by position
      const differentPositions = dropZoneIndices.filter((val, idx) => val !== idx).length;
      hasValidShuffle = differentPositions >= Math.ceil(dropZoneIndices.length / 2);
      maxAttempts--;
    }

    // If still identity after max attempts (very unlikely), force a rotation
    const isIdentity = dropZoneIndices.every((val, idx) => val === idx);
    if (isIdentity && dropZoneIndices.length > 1) {
      // Rotate array by 1 position to break correlation
      const first = dropZoneIndices.shift()!;
      dropZoneIndices.push(first);
    }

    setShuffledDropZoneIndices(dropZoneIndices);
  }, [content?.pairs, content?.items, content?.targets, content?.correct_matches, cardId]);

  // Check if all pairs are matched correctly
  // Uses label-based matching to support duplicate target labels
  const checkAllMatched = useCallback(() => {
    if (shuffledPairs.length === 0) {
      setAllMatched(false);
      return;
    }

    // Get all placed items and their drop zone labels
    const placedItems = new Set<string>();
    const placedItemLabels: Record<string, string> = {};

    // For each match entry, extract the item and the drop zone's label
    Object.entries(matches).forEach(([dropZoneId, itemId]) => {
      if (itemId) {
        placedItems.add(itemId);
        // Extract the label from the dropZoneId (format: "label-index")
        const dropZonePair = shuffledPairs.find(
          (pair, index) => `${pair?.match}-${index}` === dropZoneId
        );
        if (dropZonePair) {
          placedItemLabels[itemId] = dropZonePair.match;
        }
      }
    });

    // Check if all items are placed AND each is in a drop zone with the correct label
    const allCorrect = shuffledPairs.every((pair) => {
      const itemId = pair?.item;
      if (!placedItems.has(itemId)) return false; // Item not placed yet
      const placedLabel = placedItemLabels[itemId];
      return placedLabel === pair?.match; // Check if placed in correct label zone
    });

    setAllMatched(allCorrect);
  }, [shuffledPairs, matches]);

  // Update allMatched when matches change
  useEffect(() => {
    checkAllMatched();
  }, [checkAllMatched]);

  // Update card state when all matched
  useEffect(() => {
    if (allMatched) {
      onCardStateChange?.(true, true);
      if (!isCardCompleted(cardId)) {
        const mistakes = getMistakes(cardId) || 0;
        updateCardScore(cardId, mistakes, maxScore);
      }
    } else {
      // Disable Next button until exercise is completed
      onCardStateChange?.(false, false);
    }
  }, [
    allMatched,
    onCardStateChange,
    isCardCompleted,
    cardId,
    getMistakes,
    updateCardScore,
    maxScore,
  ]);

  // Hide instruction after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInstruction(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Handle drag start
  const handleDragStart = useCallback((event: any) => {
    setActiveId(event?.active?.id as string);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over) return;

      const itemId = active?.id as string;
      const dropZoneId = over?.id as string;

      // Check if drop zone is already locked (correctly matched)
      if (feedback[dropZoneId] === "correct") {
        return; // Don't allow drops on locked zones
      }

      // Find the drop zone's label from the dropZoneId
      const dropZonePair = shuffledPairs.find(
        (pair, index) => `${pair?.match}-${index}` === dropZoneId
      );
      const dropZoneLabel = dropZonePair?.match;

      // Find the dragged item's correct match label
      const draggedItemPair = shuffledPairs.find(pair => pair?.item === itemId);
      const itemCorrectLabel = draggedItemPair?.match;

      // Check if the drop zone label matches the item's correct label
      // This allows items to be dropped on ANY drop zone with the matching label
      // (handles duplicate labels like multiple "Appropriate for Staff" zones)
      const isCorrect = dropZoneLabel === itemCorrectLabel;

      if (isCorrect) {
        // Correct match
        setMatches((prev) => {
          const newMatches = { ...prev, [dropZoneId]: itemId };
          return newMatches;
        });
        setFeedback((prev) => ({ ...prev, [dropZoneId]: "correct" }));
      } else {
        // Incorrect match -> count as mistake before completion
        if (!isCardCompleted(cardId)) {
          incrementMistakes(cardId, maxScore);
        }

        setFeedback((prev) => ({ ...prev, [dropZoneId]: "incorrect" }));

        // Reset after 2 seconds
        setTimeout(() => {
          setFeedback((prev) => ({ ...prev, [dropZoneId]: null }));
        }, 2000);
      }
    },
    [
      shuffledPairs,
      feedback,
      isCardCompleted,
      cardId,
      incrementMistakes,
      maxScore,
    ]
  );

  // Get draggable items (unmatched items)
  const getDraggableItems = useCallback(() => {
    const matchedItems = Object.values(matches);
    return (shuffledPairs || []).filter(
      (pair) => !matchedItems?.includes(pair?.item)
    );
  }, [shuffledPairs, matches]);

  // Get drop zones - use shuffledDropZoneIndices to display in different order than draggable items
  // This prevents the "too easy" problem where items and drop zones are positionally correlated
  const getDropZones = useCallback(() => {
    // Use the shuffled indices to reorder drop zones independently of draggable items
    const indicesToUse = shuffledDropZoneIndices.length === shuffledPairs.length
      ? shuffledDropZoneIndices
      : shuffledPairs.map((_, i) => i); // Fallback to original order if indices not ready

    return indicesToUse
      .map((originalIndex) => {
        const pair = shuffledPairs[originalIndex];
        if (!pair) return null;
        const dropZoneId = `${pair.match}-${originalIndex}`;
        const matchedItemId = matches[dropZoneId] || null;

        // Get the correct feedback for the MATCHED item (not the zone's original pair)
        // This is important for duplicate labels where different items may have different feedback
        let itemFeedback = pair.correct_feedback;
        if (matchedItemId) {
          const matchedPair = shuffledPairs.find(p => p?.item === matchedItemId);
          if (matchedPair) {
            itemFeedback = matchedPair.correct_feedback;
          }
        }

        return {
          id: dropZoneId, // Keep using original index in ID for correct matching logic
          label: pair.match,
          matchedItem: matchedItemId,
          isCorrect: feedback[dropZoneId] === "correct",
          isIncorrect: feedback[dropZoneId] === "incorrect",
          isLocked: feedback[dropZoneId] === "correct", // Lock when correct
          correctFeedback: itemFeedback, // Pass feedback for the actual matched item
        };
      })
      .filter((zone): zone is NonNullable<typeof zone> => zone !== null);
  }, [shuffledPairs, shuffledDropZoneIndices, matches, feedback]);

  return {
    activeId,
    allMatched,
    showInstruction,
    handleDragStart,
    handleDragEnd,
    getDraggableItems,
    getDropZones,
  };
};
